import React from "react"
import styled, {css} from "styled-components"

import {PerkRedemptionsReport} from "@irati/types"
import {Col, Row} from "@irati/ui/src/ColumnRow"
import {Body, H4, H5, H6} from "@irati/ui/src/Typography"
import FormattedMessage, {FormattedCurrency} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import {getTypeIcon} from "./BreakdownDetails"

interface Props {
  data?: PerkRedemptionsReport[]
  showRedemptionCount?: boolean
  showSavings?: boolean
  showRanking?: boolean
}

const SavingsTable = ({data = [], showSavings, showRedemptionCount, showRanking}: Props) => {
  return (
    <>
      <HeaderRow>
        {showRanking && (
          <Col xs={1}>
            <H6>#</H6>
          </Col>
        )}
        <Col xs={showRanking ? 4 : 5}>
          <H6>
            <FormattedMessage id="platform.mySavings.userTable.headers.perk" />
          </H6>
        </Col>
        <Col xs={4}>
          <H6>
            <FormattedMessage id="platform.mySavings.userTable.headers.type" />
          </H6>
        </Col>
        {showSavings && (
          <Col xs={3}>
            <H6 ellipsis>
              <FormattedMessage id="platform.mySavings.userTable.headers.savings" />
            </H6>
          </Col>
        )}
        {showRedemptionCount && (
          <Col xs={3}>
            <H6 ellipsis>
              <FormattedMessage id="platform.mySavings.userTable.headers.redemptions" />
            </H6>
          </Col>
        )}
      </HeaderRow>
      {data.map((item, i) => (
        <TableRow key={`table-${i}`}>
          {showRanking && (
            <Col xs={1}>
              <Body small>{i + 1}</Body>
            </Col>
          )}
          <Col xs={showRanking ? 4 : 5}>
            <Flex direction="column">
              {item.brandName && <H5 color={mainTheme.colors.greys.grey50}>{item.brandName}</H5>}
              <Body small ellipsis>
                {item.header}
              </Body>
            </Flex>
          </Col>
          <Col xs={4}>
            <Flex alignItems="center">
              {getTypeIcon(item.type)}&nbsp;
              <Body small ellipsis>{getPerksReportTypeLabel(item.type)}</Body>
            </Flex>
          </Col>
          {showSavings && (
            <Col xs={3}>
              <H4>
                <FormattedCurrency currency={item.totalSavings.currency} value={item.totalSavings.value} isCents={false} />
              </H4>
            </Col>
          )}
          {showRedemptionCount && (
            <Col xs={3}>
              <Body small>{item.redemptions}</Body>
            </Col>
          )}
        </TableRow>
      ))}
    </>
  )
}

export default SavingsTable

const HeaderRow = styled(Row)`
  ${({theme}) => css`
    border-bottom: 1px solid ${theme.colors.greys.black};
    padding: ${theme.spacing.two} 0;
    margin: ${theme.spacing.one} 0;
  `}
`
const TableRow = styled(Row)`
  ${({theme}) => css`
    border-bottom: 1px solid ${theme.colors.greys.grey20};
    padding: ${theme.spacing.two} 0;
    margin: ${theme.spacing.one} 0;
    &:last-child {
      border-bottom: none;
    }
  `}
`

const getPerksReportTypeLabel = (type: string) => {
  switch (type) {
    case "company benefit":
      return <FormattedMessage id="platform.mySavings.userTable.type.companyBenefit" />
    case "flexi_perk":
      return <FormattedMessage id="platform.mySavings.userTable.type.flexiPerk" />
    case "gift_card":
      return <FormattedMessage id="platform.mySavings.userTable.type.giftCard" />
    case "reward":
      return <FormattedMessage id="platform.mySavings.userTable.type.rewardPoints" />
    case "specialRates":
      return <FormattedMessage id="platform.mySavings.userTable.type.specialRates" />
    case "competition":
      return <FormattedMessage id="platform.mySavings.userTable.type.competition" />
    case "% discount voucher":
      return <FormattedMessage id="platform.mySavings.userTable.type.percDiscount" />
    case "discount voucher":
      return <FormattedMessage id="platform.mySavings.userTable.type.discount" />
    case "free":
      return <FormattedMessage id="platform.mySavings.userTable.type.free" />
    default:
      return type
  }
}
